import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const SalePaymentEdit = ({ open, onClose, editData, token, onSuccess }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/customer-balance-payments",
    token
  );

  const { data: customersData, loading: customersLoading } = useFetchObjects(
    ["customers"],
    "shop/customers?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const customers = customersData?.results || customersData || [];
  const currencies = currenciesData?.results || currenciesData || [];

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (editData) {
      setAmount(editData.amount ?? "");
      setPaymentDate(
        editData.payment_date ? editData.payment_date.slice(0, 16) : ""
      );
      setNotes(editData.notes || "");
      if (editData.customer_details) {
        setSelectedCustomer({
          id: editData.customer_details.id,
          name: editData.customer_details.name,
        });
      } else if (editData.customer) {
        const c = customers.find((x) => x.id === editData.customer);
        if (c) setSelectedCustomer(c);
      }
      if (editData.currency_details) {
        setSelectedCurrency({
          id: editData.currency_details.id,
          title: editData.currency_details.title,
        });
      } else if (editData.currency && currencies.length) {
        const curr = currencies.find((x) => x.id === editData.currency);
        if (curr) setSelectedCurrency(curr);
      }
    }
  }, [editData, customers, currencies]);

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      onClose();
    }
  }, [isSuccess, onClose, onSuccess]);

  const handleSubmit = () => {
    if (!editData?.id || !selectedCustomer?.id || !selectedCurrency?.id || !amount)
      return;
    const payload = {
      customer: selectedCustomer.id,
      currency: selectedCurrency.id,
      amount: Number(amount),
      payment_date: paymentDate,
      notes: notes.trim() || undefined,
    };
    handleUpdate(editData.id, payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("salePayment.editPayment", "Edit payment")}
      </DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedCustomer}
            onChange={(e, newValue) => setSelectedCustomer(newValue)}
            options={customers || []}
            getOptionLabel={(option) => option?.name ?? "—"}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={customersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.customer", "Customer")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            value={selectedCurrency}
            onChange={(e, newValue) => setSelectedCurrency(newValue)}
            options={currencies || []}
            getOptionLabel={(option) => option?.title ?? ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={currencyLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.selectCurrency", "Currency")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {currencyLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            margin="dense"
            label={t("salePayment.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            margin="dense"
            label={t("salePayment.paymentDate", "Payment date")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("salePayment.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedCustomer || !selectedCurrency || !amount
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("salePayment.submit", "Save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalePaymentEdit;
